import { Table, Header, Box, Pagination, Link } from "@amzn/awsui-components-react";
import { useMemo, useState } from "react";
import { DLQRedriveInfoResult, Stage } from "../../../lusca-search-api/generated-src";
import { generateS3PrefixUrl } from "../../../helpers/logUtils";

interface DlqRedriveDetailsTableProps {
  details: DLQRedriveInfoResult;
  loading: boolean;
  stage: Stage;
}

const PAGE_SIZE = 50;

export function DlqRedriveDetailsTable({ details, loading, stage }: DlqRedriveDetailsTableProps) {
  const items = useMemo(
    () =>
      details.result?.redrivenMessages
        ? Object.entries(details.result.redrivenMessages).map(([messageId, status]) => ({
            messageId,
            status: String(status),
          }))
        : [],
    [details.result?.redrivenMessages]
  );

  const [currentPageIndex, setCurrentPageIndex] = useState(1);
  const totalPages = Math.max(1, Math.ceil(items.length / PAGE_SIZE));
  // If the page index is now beyond the data (e.g. switched to a smaller
  // execution), fall back to the last valid page instead of an empty one.
  const pageIndex = Math.min(currentPageIndex, totalPages);
  const paginatedItems = useMemo(() => {
    const start = (pageIndex - 1) * PAGE_SIZE;
    return items.slice(start, start + PAGE_SIZE);
  }, [items, pageIndex]);

  const resultsConsoleUrl = details.result?.resultsS3Uri
    ? generateS3PrefixUrl(stage, details.result.resultsS3Uri)
    : null;
  const truncatedDescription = details.result?.truncated ? (
    <>
      Showing the first 1000 results; this redrive processed more.{" "}
      {resultsConsoleUrl ? (
        <Link external href={resultsConsoleUrl}>
          View the full result files in S3
        </Link>
      ) : (
        "See the S3 result files for the full set."
      )}
    </>
  ) : undefined;

  return (
    <Table
      resizableColumns
      stickyHeader
      columnDefinitions={[
        {
          id: "messageId",
          header: "Message ID",
          cell: (item) => item.messageId,
          sortingField: "messageId",
        },
        {
          id: "status",
          header: "Status",
          cell: (item) => item.status,
          sortingField: "status",
        },
      ]}
      items={paginatedItems}
      loading={loading}
      loadingText="Loading messages"
      header={
        <Header
          variant="awsui-h1-sticky"
          counter={
            items.length > 0
              ? // "+" signals the shown rows are a capped subset, not the true total.
                `(${items.length}${details.result?.truncated ? "+" : ""})`
              : undefined
          }
          description={truncatedDescription}
        >
          Redriven Messages
        </Header>
      }
      empty={
        <Box textAlign="center">
          <Box variant="p">No messages found</Box>
        </Box>
      }
      pagination={
        items.length > PAGE_SIZE ? (
          <Pagination
            currentPageIndex={pageIndex}
            pagesCount={totalPages}
            onChange={({ detail }) => setCurrentPageIndex(detail.currentPageIndex)}
          />
        ) : undefined
      }
    />
  );
}
